/*

& OTP waits for the web login UI (LoginModal, LoginCard).

  The server limits codes per phone (lib/sendOTP.ts: 30 seconds apart, 5 an hour) and locks
  a phone after 5 wrong codes (lib/verifyOTP.ts) until its codes expire. It tells the
  client how long to wait in two ways, and these helpers read both:

    - send-otp answers HTTP 429 with `{ error: "send_otp_rate_limiting_in_effect",
      retry_after_seconds }`;
    - a locked sign-in comes back from NextAuth as `error: "too_many_attempts:<seconds>"`
      (lib/auth/authorize.ts throws it; NextAuth passes the message through).

  A wrong code comes back as `error: "invalid_otp:<attempts left>"`, or bare "invalid_otp"
  when the server counted nothing (no live code).

  The copy matches the Android app's, so both platforms say the same thing. No message ends
  in a full stop.

*/

/** The prefix lib/auth/authorize.ts throws for a locked phone, followed by the seconds left. */
export const TOO_MANY_ATTEMPTS_PREFIX = "too_many_attempts:";

/** The code lib/auth/authorize.ts throws for a wrong OTP, optionally followed by ":<attempts left>". */
export const INVALID_OTP_CODE = "invalid_otp";

/** True when a NextAuth sign-in error is a wrong OTP, with or without an attempts count. */
export function isInvalidOtpSignInError(error: string | null | undefined): boolean {
	return !!error && (error === INVALID_OTP_CODE || error.startsWith(`${INVALID_OTP_CODE}:`));
}

/** The error code without the ":<n>" suffix, for ErrorMap lookups and setErrorCode. */
export function signInErrorCode(error: string): string {
	return isInvalidOtpSignInError(error) ? INVALID_OTP_CODE : error;
}

/** "Invalid OTP. 3 attempts left", or the plain retry line when the count is unknown. */
export function invalidOtpMessage(error: string | null | undefined): string {
	const left = parseInt((error || "").slice(INVALID_OTP_CODE.length + 1), 10);
	if (!Number.isFinite(left) || left < 1) return "Invalid OTP. Please try again";
	return `Invalid OTP. ${left} ${left === 1 ? "attempt" : "attempts"} left`;
}

/** Fallback when the server refuses a send without saying how long: its own minimum. */
export const DEFAULT_SEND_WAIT_SECONDS = 30;

/** Fallback when a lock arrives without a wait: the code lifetime. */
export const DEFAULT_LOCK_SECONDS = 300;

/** "45s" / "4m 32s" / "56m": seconds are dropped from 10 minutes up, where they only flicker. */
export function formatCooldown(totalSeconds: number): string {
	const seconds = Math.max(0, Math.floor(totalSeconds));
	const minutes = Math.floor(seconds / 60);
	const remainder = seconds % 60;
	if (minutes >= 10) return `${minutes}m`;
	if (minutes > 0 && remainder > 0) return `${minutes}m ${remainder}s`;
	if (minutes > 0) return `${minutes}m`;
	return `${remainder}s`;
}

/** Seconds left on a lock, from a NextAuth sign-in error, or null when it isn't one. */
export function lockSecondsFromSignInError(error: string | null | undefined): number | null {
	if (!error || !error.startsWith(TOO_MANY_ATTEMPTS_PREFIX)) return null;
	const seconds = parseInt(error.slice(TOO_MANY_ATTEMPTS_PREFIX.length), 10);
	return Number.isFinite(seconds) && seconds > 0 ? seconds : DEFAULT_LOCK_SECONDS;
}

/** `retry_after_seconds` from a 429 send-otp body, or the fallback. */
export function sendWaitSecondsFromBody(body: any): number {
	const seconds = Number(body?.retry_after_seconds);
	return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : DEFAULT_SEND_WAIT_SECONDS;
}

/** Message while a send is refused, with the live wait. */
export function tooManyCodesMessage(secondsLeft: number): string {
	return `Too many codes requested for this number. Try again in ${formatCooldown(secondsLeft)}`;
}

/** Message while the phone is locked for wrong codes, with the live wait. */
export function tooManyAttemptsMessage(secondsLeft: number): string {
	return `Too many incorrect attempts. Try again in ${formatCooldown(secondsLeft)}`;
}
